import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { TOUCH_TARGET_MIN } from "../../theme/tokens";

/**
 * Grows a control to `TOUCH_TARGET_MIN` on coarse pointers only, so desktop density is untouched.
 * `min*` means it can only ever grow a control — safe to merge onto one that already meets the size.
 *
 * OPT-IN, never a shared base: `min-height` always clamps `height`, so applying this inside a widely
 * used primitive silently breaks the 40px row-alignment invariant `CONTROL_HEIGHT.STANDARD` documents —
 * on every tablet and touch-capable laptop, not just phones. Reach for it at the call site that needs a
 * finger target, or inside a component only its own feature consumes.
 *
 * Typed `SystemStyleObject`, not `SxProps`: the latter may itself be an array and MUI rejects a nested
 * one, and this has to stay usable as `sx={[base, TOUCH_TARGET_SX]}`.
 */
export const TOUCH_TARGET_SX: SystemStyleObject<Theme> = {
	"@media (pointer: coarse)": { minWidth: TOUCH_TARGET_MIN, minHeight: TOUCH_TARGET_MIN },
};

/**
 * The height half of `TOUCH_TARGET_SX`, for controls that share a row's width between them.
 *
 * A row of N equal cells (a 7-day strip, a segmented control) cannot also give every cell a 44px
 * minimum WIDTH: on a 320–390px phone that sum overflows the screen, and only on coarse pointers, so a
 * desktop browser at the same width never shows it. The layout decides the width; this keeps the
 * finger-sized height the width had to give up.
 */
export const TOUCH_TARGET_HEIGHT_SX: SystemStyleObject<Theme> = {
	"@media (pointer: coarse)": { minHeight: TOUCH_TARGET_MIN },
};
